/**
 * `entities/taxonomy-list` query options factories.
 *
 * Direct hey-api client calls by URL, same convention as `entities/taxonomy`.
 * `assetsHttp` auto-injects `x-selected-workspace-id`, so workspace context is implicit.
 */
import { queryOptions } from '@tanstack/react-query';
import { assetsHttp } from '@/lib/api/http-client';
import { suppressErrorNotificationFor } from '@/lib/api/interceptors/problem-details.interceptor';
import { parseResponse } from '@/lib/api/parse-response';
import { TAXONOMY_LIST_QUERY_KEYS } from './query-keys';
import {
  blockingSchemasSchema,
  standardListCatalogSchema,
  taxonomyListChangeLogSchema,
  taxonomyListDetailSchema,
  taxonomyListImpactSchema,
  taxonomyListIndexSchema,
  taxonomyListUsageSchema,
} from './schemas';

const LISTS_URL = '/api/v1/taxonomy-lists';

export const taxonomyListsQueryOptions = () =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.list(),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({ throwOnError: true, url: LISTS_URL, signal });
      return parseResponse(taxonomyListIndexSchema, response);
    },
  });

/**
 * `suppressErrorStatuses` is for a caller that reports those failures itself. Best-effort: the
 * cache entry is shared, and a fetch the cache starts on its own (an invalidation refetch, or one
 * another observer joins) runs whichever observer's `queryFn` rendered last, so it may toast.
 */
export const taxonomyListDetailQueryOptions = (
  id: string,
  { suppressErrorStatuses }: { suppressErrorStatuses?: readonly number[] } = {},
) =>
  queryOptions({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- one entry per list: the header changes who toasts, not what is fetched, and a shared entry makes that best-effort
    queryKey: TAXONOMY_LIST_QUERY_KEYS.detail(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}`,
        path: { id },
        headers: suppressErrorNotificationFor(suppressErrorStatuses),
        signal,
      });
      return parseResponse(taxonomyListDetailSchema, response);
    },
  });

/** How many live schemas bind a field to this list — gates deletion in the UI. */
export const taxonomyListUsageQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.usage(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/usage`,
        path: { id },
        signal,
      });
      return parseResponse(taxonomyListUsageSchema, response).count;
    },
  });

/** The list's append-only audit trail — who moved the vocabulary, when and why. */
export const taxonomyListChangeLogQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.changeLog(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/change-log`,
        path: { id },
        signal,
      });
      return parseResponse(taxonomyListChangeLogSchema, response);
    },
  });

/**
 * Blast radius: which schemas bind to this list and which will actually change
 * when a new version is published (following) versus stay frozen (pinned).
 */
export const taxonomyListImpactQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.impact(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/impact`,
        path: { id },
        signal,
      });
      return parseResponse(taxonomyListImpactSchema, response);
    },
  });

/** The Standard Lists catalog, flagged with what this org already holds. */
export const taxonomyStandardListCatalogQueryOptions = () =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.standardCatalog(),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/standard-catalog`,
        signal,
      });
      return parseResponse(standardListCatalogSchema, response);
    },
  });

/**
 * The schemas a delete of this list is refused for. Unlike the impact preview,
 * this counts published snapshots as well as head versions, as the server's
 * delete guard does.
 */
export const taxonomyListBlockingSchemasQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_LIST_QUERY_KEYS.blockingSchemas(id),
    // Refetched on every open; the dialog waits for that answer, not a cached one.
    staleTime: 0,
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${LISTS_URL}/{id}/blocking-schemas`,
        path: { id },
        signal,
      });
      // Parsed so a response the dialog cannot read shows its error, never "unused".
      return parseResponse(blockingSchemasSchema, response).data;
    },
  });
